import axios from "axios"

import type { DateRange } from "@/features/admin-dashboard/utils/date-ranges"

import { resolveDateRange } from "@/features/admin-dashboard/utils/date-ranges"
import { api } from "@/lib/api-client"

/**
 * Build query string for date range parameter
 */
export function buildDateRangeQuery(dateRange: DateRange): string {
  const { startDate, endDate } = resolveDateRange(dateRange)
  return `start_date=${encodeURIComponent(startDate)}&end_date=${encodeURIComponent(endDate)}`
}

// Access Policy Management (users + groups)
export type AccessControlList = {
  oids?: string[]
  groups?: string[]
}

export type AccessPolicy = {
  requires?: AccessControlList
  block?: AccessControlList
  unrestricted?: boolean
}

export type AccessPolicyUpdate = {
  requires: { oids: string[]; groups: string[] }
  block: { oids: string[]; groups: string[] }
  unrestricted: boolean
}

// =============================================================================
// Raw-entity API — generic helpers for the Raw JSON tab.
// =============================================================================
//
// Every admin-managed entity (routes, models, features, tools, MCPs,
// training modules, libraries) exposes a paired GET/PUT endpoint at
// ``/{entity-plural}/<id>/raw`` that returns/replaces the full stored
// JSON object with an ``expected_hash`` optimistic-concurrency guard
// and a mandatory ``reason`` (>= 10 chars) recorded in the audit trail.
// The backend handler lives in ``routes/admin/_helpers.py``
// (``handle_raw_entity_get`` / ``handle_raw_entity_put``).

export type RawEntityResponse<T = Record<string, unknown>> = {
  entity: T | null
  hash: string
}

export type RawEntitySaveArgs = {
  endpoint: string
  entity: Record<string, unknown>
  expectedHash: string
  reason: string
  source?: string
}

/**
 * Fetch the current entity + hash from a raw endpoint.
 *
 * ``endpoint`` is the full path under /admin (e.g.
 * ``/admin/routes/advantum/raw``). The caller is responsible for any
 * url-encoding of path segments.
 */
export async function getRawEntity<T = Record<string, unknown>>(
  endpoint: string
): Promise<RawEntityResponse<T>> {
  const response = await api.get(endpoint)
  return response.data || response
}

/**
 * Rethrow an optimistic-concurrency rejection in the shared shape: a 409 with
 * ``code: "hash_mismatch"`` becomes an Error whose message starts with
 * ``"hash_mismatch"`` and whose ``cause`` carries ``{ code, currentHash }``
 * for callers that want to offer a "reload" prompt. Every other error is
 * rethrown untouched.
 */
export function rethrowHashMismatch(
  error: unknown,
  fallbackMessage = "modified by someone else"
): never {
  if (
    axios.isAxiosError(error) &&
    error.response?.status === 409 &&
    error.response.data?.code === "hash_mismatch"
  ) {
    const currentHash =
      typeof error.response.data?.current_hash === "string"
        ? error.response.data.current_hash
        : undefined
    throw new Error(`hash_mismatch: ${error.response.data?.error ?? fallbackMessage}`, {
      cause: { code: "hash_mismatch", currentHash },
    })
  }
  throw error
}

/**
 * Save the full entity via the raw endpoint with optimistic concurrency.
 *
 * Returns 409 from the server if the stored hash has moved since the
 * caller last read it. Axios throws on non-2xx, so we catch the 409 and
 * rethrow via {@link rethrowHashMismatch}.
 */
export async function saveRawEntity<T = Record<string, unknown>>(
  args: RawEntitySaveArgs
): Promise<{ success: boolean; entity: T; hash: string }> {
  try {
    const response = await api.put(args.endpoint, {
      entity: args.entity,
      expected_hash: args.expectedHash,
      reason: args.reason,
      source: args.source ?? "raw",
    })
    const data = response.data || response
    if (data.success === false) {
      throw new Error(data.message || data.error || "Raw save failed")
    }
    return data
  } catch (error) {
    rethrowHashMismatch(error, "entity was modified by someone else")
  }
}
